import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@material-ui/lab';
import { flatten, isArray, some } from 'lodash-es';
import { parse } from 'query-string';
import styled from 'styled-components';

import { DeliveryLogType } from '@dev/ee-api/ee-api-models';
import { FilterIcon, FiltersPopover, palette, SimplePopover, Tag } from '@dev/ee-components';

import { safelyDecodeBase64Url } from '@dev/ee-tools';
import dayjs from '@/services/tools/dayjs';
import { setAtEndOfDay } from '@/services/tools/handleUrlParams';
import useMobile from '@/services/hooks/useMobile';
import { GetChannelEventsCounts, getReportsState } from '@/store/reports/actions';

import { EventFilterWrapper, FilterContainer, FilterLabel } from './styledComponents';
import { CustomPopoverBodyInterface, EventFilterInterface, EventsFiltersTypeEnum } from './Interfaces';
import { useDefaultLogFilters } from './useDefaultLogFilters';
import { convertMessageCategoryAndStatuses } from './helperFunctions';
import { useEventsFilterOptions } from './useEventsFilterOptions';
import { useMobileFilterTabs } from '../../CampaignReport/Activity/hooks/useMobileFiltersTab';

const FiltersPopoverContainer = styled.div<{ hasMessageCategory: boolean }>`
    background: ${palette.basic.paper};
    border: 1px solid ${palette.indigo[6]};
    border-radius: 4px;
    margin-left: 8px;
    height: 44px;
    width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;

    .MuiPaper-root:first-of-type {
        width: 100%;
        min-width: 250px;
        display: inline-grid;
    }

    ${({ hasMessageCategory }) =>
        !hasMessageCategory &&
        `
        .optionWithCheckbox {
            .optionTag {
                margin-right: 12px;
            }
        }
       
    `}
`;

const loadingOptions = new Array(8).fill({ label: '', value: '' });

const CustomPopoverBody: FC<CustomPopoverBodyInterface> = ({
    element,
    isLoading,
}: CustomPopoverBodyInterface): JSX.Element => {
    const { t } = useTranslation();
    return (
        <FilterContainer>
            {isLoading ? (
                <Skeleton width="100%" />
            ) : (
                <>
                    <FilterLabel>{element?.isEmpty ? t('noeventssavailable') : element?.label}</FilterLabel>
                    {element.count && <Tag label={element.count} clickable={false} />}
                </>
            )}
        </FilterContainer>
    );
};

const setDefaultSelectedFilters = () => {
    const query = parse(location.search, { arrayFormat: 'comma' });
    const urlStatuses: string[] = query.events ? flatten([query.events]) : [];
    const urlResult: string[] = query.result ? flatten([query.result]) : [];

    if ((!query.events && !query.result) || urlStatuses.includes(DeliveryLogType.All)) {
        return [];
    }
    const [statuses, results] = convertMessageCategoryAndStatuses(urlStatuses as any, urlResult as any);

    return [...statuses, ...results];
};

export const EventFilter: FC<EventFilterInterface> = ({
    onHeadersEventFilterClick,
    eventFiltersType,
    timeRangeValue,
}: EventFilterInterface): JSX.Element => {
    const options = useEventsFilterOptions(eventFiltersType);

    const isInProgressLogs = eventFiltersType === EventsFiltersTypeEnum.InProgress;
    const location = useLocation();
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const { t } = useTranslation();
    const { countsLoading } = useSelector(getReportsState);
    /**should not fetch data on inprogress screen */
    const [shouldRefetchData, setShouldRefechData] = useState(!isInProgressLogs);
    const params = useParams<{
        name: string;
    }>();

    const query = parse(location.search, { parseBooleans: true });

    const [selected, setSelected] = useState(setDefaultSelectedFilters);
    const channelName = safelyDecodeBase64Url(params.name);
    useEffect(() => {
        !isInProgressLogs && setShouldRefechData(true);
    }, [query.email, query.from, query.to, query.lastEvent]);

    const timeRangeRef = useRef(timeRangeValue);
    const defaultLogFilters = useDefaultLogFilters(isInProgressLogs);
    const isLastEvent = (query?.lastEvent as boolean) ?? true;
    const mobileFiltersTabs = useMobileFilterTabs(timeRangeRef, selected, isLastEvent);

    if (!onHeadersEventFilterClick) {
        return null;
    }

    const onChangeSelection = (_ev, selected) => {
        setSelected(selected);
        onHeadersEventFilterClick(selected);
    };

    const fetchCountsForEvents = (): void => {
        if (!shouldRefetchData || isInProgressLogs) {
            return null;
        }

        const dateParam = {
            ...defaultLogFilters,
            ...(query?.from &&
                query?.to && {
                    from: dayjs.utc(query?.from as string).toJSON(),
                    to: setAtEndOfDay(dayjs.utc(query?.to as string).toJSON()),
                }),
        };
        const params = {
            ...dateParam,
            email: query.search as string,
            channelName: (query.channel as string) || channelName,
            includeEmail: true,
            lastEvent: isLastEvent,
        };

        dispatch(GetChannelEventsCounts(params));
        setShouldRefechData(false);
    };

    const isEventsLoading = shouldRefetchData || countsLoading;

    const dropdownOptions = isEventsLoading ? loadingOptions : options;
    const hasMessageCategory = isEventsLoading || some(dropdownOptions, isArray);
    if (isMobile) {
        return (
            <FiltersPopoverContainer {...{ hasMessageCategory }}>
                <FiltersPopover
                    onApply={(obj, dateFrom, dateTo): void => {
                        setSelected(obj.event);
                        timeRangeRef.current = obj.date;
                        onHeadersEventFilterClick(obj.event, obj.date, dateFrom, dateTo);
                    }}
                    tabs={mobileFiltersTabs}
                    title={t('filters')}
                    translations={{
                        apply: t('apply'),
                        close: t('close'),
                    }}
                >
                    <FilterIcon width={20} height={20} onClick={fetchCountsForEvents} />
                </FiltersPopover>
            </FiltersPopoverContainer>
        );
    }

    return (
        <EventFilterWrapper>
            <SimplePopover
                type="icon"
                options={dropdownOptions}
                checkboxValue={selected}
                // eslint-disable-next-line react/no-unstable-nested-components
                customDropdownBody={(element): JSX.Element => (
                    <CustomPopoverBody
                        isLoading={isEventsLoading}
                        element={element as { count: number; label: string; value: string }}
                    />
                )}
                enableCheckbox
                onCheckboxClick={onChangeSelection}
                groupNameWithCheckbox
                listItemWidth={isInProgressLogs ? 200 : 350}
                maxHeight={300}
            >
                <FilterIcon onClick={fetchCountsForEvents} />
            </SimplePopover>
        </EventFilterWrapper>
    );
};
